/** @file renderMethods: server-side renders as signed image URLs, and fetching those images. */
import type { RenderQuery, RenderReply } from '../types/rest';
import type { ClientOptions } from './clientOptions';
import { fillPath } from './fillPath';
import { request } from './request';
import { toRenderParams } from './toRenderParams';

/** A render reply plus the cache outcome (`X-Pose3D-Cache`) and the document hash it rendered. */
export type RenderResultWire = RenderReply & { cache: 'hit' | 'miss'; docHash: string };

/** The render members of the client. */
export type RenderMethods = {
  /** `GET /api/v1/puppets/{id}/render`. */
  render: (puppetId: string, query: RenderQuery) => Promise<RenderResultWire>;
  /** Fetches every image URL of a render reply, in order, without credentials (the URLs are signed). */
  fetchRenderImages: (reply: Pick<RenderReply, 'images'>) => Promise<Uint8Array[]>;
};

/** Render calls over `opts`. */
export function renderMethods(opts: ClientOptions): RenderMethods {
  const anonymous: ClientOptions = { ...(opts.url === undefined ? {} : { url: opts.url }), ...(opts.fetch === undefined ? {} : { fetch: opts.fetch }) };
  return {
    render: async (id, query) => {
      const r = await request<RenderReply>(opts, {
        method: 'GET', path: fillPath('/api/v1/puppets/{id}/render', { id }), query: toRenderParams(query), accept: 'json',
      });
      const cache = r.headers.get('x-pose3d-cache') === 'hit' ? 'hit' : 'miss';
      return { ...r.value, cache, docHash: r.headers.get('x-pose3d-doc-hash') ?? '' };
    },
    fetchRenderImages: (reply) => Promise.all(reply.images.map(async (image) =>
      (await request<Uint8Array>(anonymous, { method: 'GET', path: image.url, accept: 'bytes' })).value)),
  };
}
